/**
 * DNSSEC + DANE/TLSA card.
 *
 * Renders the top-level `dnssec` block a fresh POST /scan-domain returns:
 *   - DNSSEC enabled (DS + DNSKEY both present) vs not, with enable guidance.
 *   - DS / DNSKEY presence chips (the chain-of-trust signals).
 *   - Per-MX DANE/TLSA status.
 *   - Any evaluator `issues`, rendered like every other check.
 *
 * Used by both the authed domain detail page and the public report page. The
 * field is absent on older persisted scans and on the anonymous tier, so the
 * parent only mounts this when `dnssec` is present — but we still guard inside.
 */
import { ShieldCheck, ShieldAlert, ShieldQuestion, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SeverityIcon, type DnssecResult } from '@/components/IssueList';
import { cn } from '@/lib/utils';

export function DnssecCard({ dnssec }: { dnssec: DnssecResult | undefined }) {
  if (!dnssec) return null;

  const enabled = dnssec.enabled === true;
  const checkError = dnssec.checkError === true;
  const dane = dnssec.dane ?? [];

  // Header icon mirrors the three states: signed+anchored (good), couldn't
  // check (unknown), not enabled (attention).
  const HeaderIcon = checkError ? ShieldQuestion : enabled ? ShieldCheck : ShieldAlert;
  const headerTone = checkError
    ? 'text-muted-foreground'
    : enabled
    ? 'text-success'
    : 'text-warning';

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <HeaderIcon className={cn('h-4 w-4', headerTone)} />
          DNSSEC &amp; DANE
        </CardTitle>
        <CardDescription>
          Cryptographic signing of your DNS answers (DNSSEC) and the DNSSEC-anchored
          inbound-TLS standard (DANE/TLSA) that Microsoft and Google are moving SMTP toward.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Top-line status */}
        {checkError ? (
          <div className="flex items-start gap-2 rounded-md border border-dashed p-3 text-sm">
            <ShieldQuestion className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <div>
              <div className="font-medium">Couldn&apos;t fully check DNSSEC</div>
              <p className="mt-0.5 text-muted-foreground">
                A DNS lookup failed during this scan. Re-scan to confirm — don&apos;t read this as
                &ldquo;not enabled.&rdquo;
              </p>
            </div>
          </div>
        ) : enabled ? (
          <div className="flex items-start gap-2 rounded-md border border-success/30 bg-success/5 p-3 text-sm">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-success" />
            <div>
              <div className="font-medium text-success">DNSSEC enabled</div>
              <p className="mt-0.5 text-muted-foreground">
                Your zone is signed and anchored at the parent — receivers can trust your DNS
                answers haven&apos;t been tampered with.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 p-3 text-sm">
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" />
            <div>
              <div className="font-medium text-warning">
                DNSSEC not enabled
              </div>
              <p className="mt-0.5 text-muted-foreground">
                Enable DNSSEC at your registrar. It cryptographically protects your DNS answers
                (including MX/SPF/DKIM/DMARC lookups) from tampering, and is the prerequisite for
                DANE.
              </p>
            </div>
          </div>
        )}

        {/* Chain-of-trust signals */}
        <div className="flex flex-wrap gap-2">
          <SignalChip label="DS record" present={dnssec.dsPresent} hint="anchored at parent" />
          <SignalChip label="DNSKEY" present={dnssec.dnskeyPresent} hint="zone is signed" />
          <SignalChip label="DANE" present={dnssec.daneEnabled} hint="MX advertises TLSA" />
        </div>

        {/* Per-MX DANE/TLSA */}
        {dane.length > 0 && (
          <div>
            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              DANE per MX host
            </div>
            <ul className="divide-y rounded-md border">
              {dane.map((d) => (
                <li
                  key={d.mxHost}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="font-mono text-xs">{d.mxHost}</span>
                  {d.tlsaPresent ? (
                    <Badge variant="success">
                      <Lock className="h-3 w-3" />
                      TLSA present
                    </Badge>
                  ) : (
                    <Badge variant="secondary">No TLSA</Badge>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Evaluator issues — same rendering as every other check. */}
        {dnssec.issues && dnssec.issues.length > 0 && (
          <ul className="space-y-2">
            {dnssec.issues.map((issue, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <SeverityIcon severity={issue.severity} />
                <span className="text-muted-foreground">{issue.message}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SignalChip({
  label,
  present,
  hint,
}: {
  label: string;
  present: boolean | undefined;
  hint: string;
}) {
  // Tri-state: present (green), absent (neutral), unknown (outline).
  if (present === undefined) {
    return (
      <Badge variant="outline" title={hint}>
        {label}: unknown
      </Badge>
    );
  }
  return (
    <Badge variant={present ? 'success' : 'secondary'} title={hint}>
      {label}: {present ? 'yes' : 'no'}
    </Badge>
  );
}

export default DnssecCard;
